import {
    HxAccount,
    HxCollection,
    HxCreateAccountArgs,
    HxEnums,
    HxObjectID,
    HxSecureString,
    HxSslScheme,
    SessionId,
} from '@outlook/hxcore_web';
import { getHxInstance } from './getHxInstance';
import { runInHxSession } from './runInHxSession';
import { waitForHxItemCreation } from './waitForHxItemCreation';
import { waitForInitialFolderSync } from './waitForInitialFolderSync';
import { handleHxObjectLoadResult } from 'hx-object-load-helpers';
import * as PIEAccounts from 'owa-client-pie/lib/hostaccountmethods.g';
import * as PIEAuth from 'owa-client-pie/lib/authentication.g';
import { errorThatWillCauseAlert, trace } from 'owa-trace';
import { isPieHostBridgeInitialized } from 'owa-client-pie';
import { addNewAccount } from 'hx-account';
import { setProfileStoreAccounts } from './setProfileStoreAccounts';
import { lazyLaunchAccountChooserDialog } from 'owa-account-chooser';
import { lazySetSelectedAccount } from 'owa-mail-account-store';
import { isBootFeatureEnabled } from 'owa-metatags';

// The PIE account manager will make PIE calls so we call isPieHostBridgeInitialized which
// should hook up the bridge if it is present. It is important that we not cause errors at
// load time if this code is not being used.
isPieHostBridgeInitialized();

// Provides support for adding, removing, and calling callbacks that
// use the specified argument types.
class AccountManagerCallback<ArgType> {
    private callbacks: ((args: ArgType) => void)[];

    constructor() {
        this.callbacks = [];
    }

    public addCallback(callback: (args: ArgType) => void) {
        this.callbacks.push(callback);
    }

    public removeCallback(callback: (args: ArgType) => void) {
        this.callbacks = this.callbacks.filter(value => value != callback);
    }

    public raiseCallback(args: ArgType) {
        this.callbacks.forEach(callback => callback(args));
    }
}

// Adds an account based on the authenticated information for the account
export async function addAuthenticatedAccount(
    email: string,
    webAccountId: string,
    accessToken: string,
    expiresDurationSecondsSinceZero: number,
    isAad: boolean
): Promise<HxAccount | undefined> {
    const accessTokenExpires = new Date(expiresDurationSecondsSinceZero * 1000);

    // Only Microsoft 365 Production accounts (meaning we don't support soverign domain) are
    // supported currently, so the server address is based on whether it is an active directory
    // based account or not.
    const createAccountArgs: HxCreateAccountArgs = {
        displayName: email,
        emailAddress: email,
        incomingServerAddress: isAad ? 'outlook.office365.com' : 'm.hotmail.com',
        incomingServerPort: 0,
        incomingServerSslScheme: HxSslScheme.SecureFromStart,
        incomingServerUsername: '',
        incomingServerPassword: new HxSecureString(''),
        webAccountId: webAccountId,
        targetAccountId: HxObjectID.objectIdNil,
        syncDeviceAccountTypeId: HxEnums.HxSyncDeviceAccountType.ActiveSyncExchange,
        authType: HxEnums.HxAccountAuthType.Bearer,
        userDisplayName: email,
        activeSyncDomain: '',
        outgoingServerAddress: '',
        outgoingServerPort: 0,
        outgoingServerSslScheme: HxSslScheme.SecureFromStart,
        outgoingServerUsername: '',
        outgoingServerPassword: new HxSecureString(''),
        outgoingServerAuthenticationRequired: true,
        creationState: HxEnums.HxNewAccountCreateState.ForceCreate,
        type: HxEnums.HxAccountType.HxS,
        exchangeForestType: HxEnums.HxExchangeForestType.Office365,
        skipHxAccountsAppActiveCheck: true,
        accessToken: new HxSecureString(accessToken),
        accessTokenExpiration: accessTokenExpires,
        onPremiseUriForCloudCache: '',
        primaryAccountIdForSharedAccount: HxObjectID.objectIdNil,
    };

    return addAccount(createAccountArgs);
}

// timeout waiting for the Hx account to be created
export const ACCOUNT_CREATION_TIMEOUT = 5000;

// The selected account for the Monarch instance, because the getHxAccount method does not
// currently take a mailbox the selected account will be constant throughout a session
// this value will not be changed once set
let selectedAccount: HxAccount | undefined = undefined;

// We only want to get the initial account, including possibly bootstrapping the
// initial account, to happen once so we use the promise to support getting the initial account
let initialAccountPromise: Promise<HxAccount> | undefined = undefined;

// Provides the set of accounts currently configured in Monarch
let allAccounts: HxCollection<HxAccount> | undefined = undefined;

// We only want to load the accounts once, this promise is used to ensure
// they are only loaded once.
let loadAccountsPromise: Promise<void> | undefined = undefined;

// Arguments that will be passed for the various change events
export interface AccountSelectedArgs {
    previous?: HxAccount;
    selected: HxAccount;
}

export interface AccountAddedArgs {
    added: HxAccount;
}

export interface AccountRemovedArgs {
    stableAccountId: string;
}

// Registered callbacks for account manager actions
const accountSelectedCallbacks = new AccountManagerCallback<AccountSelectedArgs>();
const accountAddedCallbacks = new AccountManagerCallback<AccountAddedArgs>();
const accountRemovedCallbacks = new AccountManagerCallback<AccountRemovedArgs>();

// Checks to see if there is a selected account without actually selecting it
export async function hasSelectedAccount(): Promise<boolean> {
    if (selectedAccount) {
        return true;
    }

    // Check with the host to see if there is a selected account
    const selectedFromHost = await PIEAccounts.getSelectedAccount();
    if (!selectedFromHost.isAccountSelected || !selectedFromHost.stableAccountObjectId) {
        // the host does not have a selected account
        return false;
    }

    const accounts = await getAccounts();
    const accountBySelectedId = getAccountByStableId(
        accounts,
        selectedFromHost.stableAccountObjectId
    );
    return accountBySelectedId ? true : false;
}

// Returns the selected account
export async function getSelectedAccount(): Promise<HxAccount> {
    if (!selectedAccount) {
        // an account is not selected, because the initial account selection will include the
        // bootstrap process, that is the configuration of the initial account, we only want to
        // do it once so that we don't create the bootstrap account multiple times. The promise
        // will also set the initial selected account.
        if (!initialAccountPromise) {
            initialAccountPromise = getInitialSelectedAccount();
        }

        return initialAccountPromise;
    }

    return selectedAccount;
}

// Return the list of accounts
export async function getAccounts(): Promise<HxCollection<HxAccount>> {
    if (!allAccounts) {
        if (!loadAccountsPromise) {
            // The call too loadAndSetAllAccounts will set allAccounts
            loadAccountsPromise = loadAndSetAllAccounts();
        }

        await loadAccountsPromise;
    }

    if (!allAccounts) {
        throw new Error('Account load failed');
    }

    return allAccounts;
}

// Adds an account to Hx based on the supplied parameters
export async function addAccount(args: HxCreateAccountArgs): Promise<HxAccount | undefined> {
    trace.verbose('adding hx account');

    const instance = getHxInstance();
    const createResult = await instance.actorAPIs.createAccount(args);
    if (!createResult || !createResult.accountId) {
        trace.verbose('adding hx account failed');
        return undefined;
    }

    const accounts = await getAccounts();

    // We need to locate the newly added account using the account Id
    let addedAccount: HxAccount | undefined = undefined;
    const filteredAccounts = accounts.items.filter(
        account => account.objectId.toString() === createResult.accountId.toString()
    );
    if (filteredAccounts.length > 0) {
        if (filteredAccounts.length > 1) {
            errorThatWillCauseAlert('FREFoundMultipleMatchingAccounts');
        }

        addedAccount = filteredAccounts[0];
    } else {
        addedAccount = await waitForHxItemCreation(accounts, ACCOUNT_CREATION_TIMEOUT);
    }

    if (addedAccount) {
        // We need to wait for the initial folder sync until the Monarch UX can support showing
        // the UX before the inital folder sync is complete.
        await waitForInitialFolderSync(addedAccount);

        // raise the notifications for client-web and the native host
        await PIEAccounts.accountAdded(
            addedAccount.displayName,
            addedAccount.emailAddress,
            addedAccount.stableAccountId
        );

        accountAddedCallbacks.raiseCallback({ added: addedAccount });
        return addedAccount;
    }

    trace.verbose('failed to find the added hx account');
    return undefined;
}

// Selects the specified account
export async function selectAccount(newSelectedAccount: HxAccount): Promise<void> {
    if (newSelectedAccount == selectedAccount) {
        errorThatWillCauseAlert('SelectSameAccount');
    }

    await PIEAccounts.accountSelected(
        newSelectedAccount.displayName,
        newSelectedAccount.emailAddress,
        newSelectedAccount.stableAccountId
    );

    const previousSelectedAccount = selectedAccount;
    selectedAccount = newSelectedAccount;

    accountSelectedCallbacks.raiseCallback({
        previous: previousSelectedAccount,
        selected: selectedAccount,
    });
}

// Deletes the specified account
export async function removeAccount(accountToDelete: HxAccount): Promise<boolean> {
    trace.verbose('deleting hx account');

    const name = accountToDelete.displayName;
    const email = accountToDelete.emailAddress;
    const stableAccountId = accountToDelete.stableAccountId;

    const instance = getHxInstance();
    const removeAccountResult = await instance.actorAPIs.removeAccount(accountToDelete.objectId);

    if (!removeAccountResult.hasActionSucceeded) {
        trace.verbose('failed to delete account');
        return false;
    }

    // Notify client-web and native host
    await PIEAccounts.accountRemoved(name, email, stableAccountId);

    accountRemovedCallbacks.raiseCallback({ stableAccountId: stableAccountId });
    return true;
}

// sets the callback for when an account is selected
export function onSelectedAccountChanged(callback: (args: AccountSelectedArgs) => void) {
    accountSelectedCallbacks.addCallback(callback);
}

export function removeOnSelectedAccountChanged(callback: (args: AccountSelectedArgs) => void) {
    accountSelectedCallbacks.removeCallback(callback);
}

// sets the callback for when an account is added
export function onAccountAdded(callback: (args: AccountAddedArgs) => void) {
    accountAddedCallbacks.addCallback(callback);
}

export function removeOnAccountAdded(callback: (args: AccountAddedArgs) => void) {
    accountAddedCallbacks.removeCallback(callback);
}

// sets the callback for when an account is removed
export function onAccountRemoved(callback: (args: AccountRemovedArgs) => void) {
    accountRemovedCallbacks.addCallback(callback);
}

export function removeOnAccountRemoved(callback: (args: AccountRemovedArgs) => void) {
    accountRemovedCallbacks.addCallback(callback);
}

// Called when the accounts are not yet loaded to load the accounts
async function loadAndSetAllAccounts(): Promise<void> {
    allAccounts = handleHxObjectLoadResult(
        await runInHxSession(async (sessionId: SessionId) => {
            const root = await getHxInstance().activeSet.getRoot(sessionId);
            return root.getAccountsSyncingMailOrCalendar(sessionId);
        })
    );

    const selectedAccountCallback = (args: AccountSelectedArgs) => {
        lazySetSelectedAccount.importAndExecute(args.selected.stableAccountId);
    };

    setProfileStoreAccounts(allAccounts);
    onSelectedAccountChanged(selectedAccountCallback);
}

// Clear the variables for the account list
async function reloadAccounts(): Promise<HxCollection<HxAccount>> {
    allAccounts = undefined;
    loadAccountsPromise = undefined;
    return getAccounts();
}

// The native host code still relies on AddAccount to set the callback via postMessage, until the
// native host authentication code is moved to PIE this call is still needed.
function stopGapUntilAuthIsMovedToPIE() {
    // This actually just sends the message to the native host
    addNewAccount();
}

// As part of the native host instance initialization it will check to see if there
// is an account or not. If there is not an account it will perform authentication
// and then wait to pass that information onto client-web so the account can be created.
// Eventually this bootstrap process will be replaced with a client-web driven process
// where we get suggestions and then add the initial account like any other account.
async function addInitialAccount(): Promise<HxAccount> {
    // Call the PIE method that is gets the initial account from the current
    // bootstrap code in Monarch, at some point this will be replaced with
    // discovery and adding the account from client-web.
    const initialAccountPromise = PIEAuth.getInitialAccountAuthToken();

    stopGapUntilAuthIsMovedToPIE();

    // wait for the PIE method to complete
    const authResult = await initialAccountPromise;
    if (!authResult.successful) {
        throw new Error('Failed to add initial account');
    }

    // There is a bug where the client-web gets reloaded during the bootstrap process
    // to work around this the native-host indicated if the request was previously
    // completed. We should only add the account when it was not previously completed.
    if (!authResult.wasPreviouslyCompleted) {
        if (
            authResult.webAccountId == undefined ||
            authResult.accessToken == undefined ||
            authResult.expiresDurationSecondsSinceZero == undefined ||
            authResult.isAad == undefined
        ) {
            throw new Error('Host failed to supply the needed paramters for a successful request');
        }

        // we only add if the auth result was not previously completed
        await addAuthenticatedAccount(
            authResult.email,
            authResult.webAccountId,
            authResult.accessToken,
            authResult.expiresDurationSecondsSinceZero,
            authResult.isAad
        );
    }

    // Reload the accounts, and if needed wait for the account to be created
    const accounts = await reloadAccounts();
    let hxAccount: HxAccount | undefined = undefined;
    if (accounts.items.length == 0) {
        hxAccount = await waitForHxItemCreation(accounts, ACCOUNT_CREATION_TIMEOUT);
    } else {
        hxAccount = accounts.items[0];
    }

    await waitForInitialFolderSync(hxAccount);
    return hxAccount;
}

// Called when there is not an account selected, if needed an account will be bootstrapped
async function getInitialSelectedAccount(): Promise<HxAccount> {
    const accounts = await getAccounts();
    if (accounts.items.length == 0) {
        selectedAccount = await addInitialAccount();
        if (selectedAccount) {
            return selectedAccount;
        }

        errorThatWillCauseAlert('FREAddAccountFailed');
        throw new Error('First Run Experience Add Account Failed');
    }

    // For now we are supporting getting the selected account from a metatag, once we can boot
    // Monarch without an account this will change to a PIE call to get the account.
    const selectedFromHost = await PIEAccounts.getSelectedAccount();
    if (selectedFromHost.isAccountSelected && selectedFromHost.stableAccountObjectId) {
        selectedAccount = getAccountByStableId(accounts, selectedFromHost.stableAccountObjectId);
        if (selectedAccount) {
            return selectedAccount;
        }
    }

    if (isBootFeatureEnabled('bootselector') && accounts.items.length > 1) {
        const selectedAccountStableId = await lazyLaunchAccountChooserDialog.importAndExecute();
        selectedAccount = getAccountByStableId(accounts, selectedAccountStableId);

        if (!selectedAccount) {
            errorThatWillCauseAlert('SelectAccountFailed');
            throw new Error('Select account failed');
        }
    } else {
        // Select the first account
        selectedAccount = accounts.items[0];
    }

    selectAccount(selectedAccount);
    return selectedAccount;
}

// Locates an account by the specified stable account id
function getAccountByStableId(
    accounts: HxCollection<HxAccount>,
    stableAccountId: string
): HxAccount | undefined {
    const filteredAccounts = accounts.items.filter(
        account => account.stableAccountId === stableAccountId
    );
    return filteredAccounts.length > 0 ? filteredAccounts[0] : undefined;
}
